import { http, queryString } from 'utils'
import { concat, omit, set, findIndex } from 'lodash'
import { UserDecorator } from 'decorators'

// Account users mutation imports.
import {
  SET_ACCOUNT_USERS, ADD_ACCOUNT_USERS, SET_ACCOUNT_USERS_PAGINATION, SET_ACCOUNT_USER,
  SET_ACCOUNT_USER_ATTRIBUTE, RESET_ACCOUNT_USER, ADD_ACCOUNT_USER
} from 'vx/mutations'

const initialAccountUserState = () => {
  return {
    id: null,
    email: null,
    mobile: null,
    role_id: null,
    profile: {
      name: null,
      surname: null,
      full_name: null
    },
    permissions: {
      load_check: false,
      invoices: false,
      payments: false,
      reports: false,
      settings: false,
      users: false,
      administrator: false
    },
    driver_profile: {
      licence: null,
      licence_expiry: null,
      photo_name: null,
      photo_storage_path: null
    }
  }
}

// Account users state.
const state = {
  accountUsers: [],
  accountUser: initialAccountUserState(),
  pagination: {}
}

// Account users state getters.
const getters = {
  accountUserDecorated: state => new UserDecorator(state.accountUser),
  haveAccountUser: state => state.accountUser.id !== null
}

// Account users actions.
const actions = {
  /**
   * Get the initial account users result.
   */
  getAccountUsers ({ commit }, { userId, payload }) {
    return http.get(`users/${userId}/account-users?${queryString(payload)}`).then(response => {
      commit(SET_ACCOUNT_USERS, response.data.data)
      commit(SET_ACCOUNT_USERS_PAGINATION, omit(response.data, 'data'))
    })
  },

  /**
   * Get the account users for the next page.
   */
  getAccountUsersForNextPage ({ commit, state }, { userId, payload }) {
    if (state.pagination.current_page === state.pagination.last_page) {
      return Promise.reject('There are no more account users to load.')
    }

    let page = state.pagination.current_page + 1

    return http.get(`users/${userId}/account-users?${queryString(payload)}&page=${page}`).then(response => {
      commit(ADD_ACCOUNT_USERS, response.data.data)
      commit(SET_ACCOUNT_USERS_PAGINATION, omit(response.data, 'data'))

      return response
    })
  },

  /**
   * Get an account user.
   */
  getAccountUser ({ commit }, id) {
    return http.get(`users/account-users/${id}`).then(response => {
      commit(SET_ACCOUNT_USER, response.data)

      return response
    })
  },

  /**
   * Store an account user.
   */
  storeAccountUser ({ commit }, payload) {
    return http.post(`users/account-users`, payload).then(response => {
      return response
    })
  },

  /**
   * Update an account user.
   */
  updateAccountUser ({ commit }, { id, payload }) {
    return http.put(`users/account-users/${id}`, payload).then(response => {
      commit(SET_ACCOUNT_USER, response.data)

      return response
    })
  },

  /**
   * Remove/archive an account user by sending a delete request.
   */
  removeUser ({ commit }, { id, payload }) {
    return http.delete(`users/account-users/${id}`, payload)
  }

}

// Account users state mutations.
const mutations = {
  /**
   * Set the account users.
   */
  [SET_ACCOUNT_USERS] (state, accountUsers) {
    state.accountUsers = accountUsers
  },

  /**
   * Set the account users pagination.
   */
  [SET_ACCOUNT_USERS_PAGINATION] (state, pagination) {
    state.pagination = pagination
  },

  /**
   * Add more account users.
   */
  [ADD_ACCOUNT_USERS] (state, accountUsers) {
    state.accountUsers = concat(state.accountUsers, accountUsers)
  },

  /**
   * Set the active account user.
   */
  [SET_ACCOUNT_USER] (state, accountUser) {
    state.accountUser = accountUser
  },

  /**
   * Set an attribute on the active account user.
   */
  [SET_ACCOUNT_USER_ATTRIBUTE] (state, { attribute, value }) {
    set(state.accountUser, attribute, value)
  },

  /**
   * Reset active account user.
   */
  [RESET_ACCOUNT_USER] (state) {
    state.accountUser = initialAccountUserState()
  },

  /**
   * Add an account user or replace an existing.
   */
  [ADD_ACCOUNT_USER] (state, accountUser) {
    let existingAccountUser = findIndex(state.accountUsers, existing => existing.id === accountUser.id)

    if (existingAccountUser === -1) {
      state.accountUsers.push(accountUser)
    } else {
      state.accountUsers.splice(existingAccountUser, 1, accountUser)
    }
  }
}

// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, state, getters, actions, mutations
}



// WEBPACK FOOTER //
// ./src/vuex/modules/user/account-users.js